import React, { createContext, useContext, useState, useCallback } from 'react';
import { searchContent } from '../services/searchService';
import { getContentByGenre } from '../services/contentService';
import type { ContentItem } from '../services/contentService';

interface SearchState {
  results: ContentItem[];
  loading: boolean;
  error: string | null;
  query: string;
  selectedCategory: string;
  selectedGenre: string;
  sortBy: 'relevance' | 'newest' | 'oldest' | 'views' | 'likes';
}

interface SearchActions {
  search: (query: string, category?: string, genre?: string, sortBy?: string) => Promise<void>;
  setQuery: (query: string) => void;
  setCategory: (category: string) => void;
  setGenre: (genre: string) => void;
  setSortBy: (sortBy: string) => void;
  clearResults: () => void;
  searchByGenre: (genre: string, category?: string) => Promise<void>;
}

interface SearchContextValue extends SearchState, SearchActions {}

const SearchContext = createContext<SearchContextValue | undefined>(undefined);

export const SearchProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [results, setResults] = useState<ContentItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedGenre, setSelectedGenre] = useState('all');
  const [sortBy, setSortBy] = useState<'relevance' | 'newest' | 'oldest' | 'views' | 'likes'>('relevance');

  const search = useCallback(async (
    searchQuery: string, 
    category: string = 'all', 
    genre: string = 'all', 
    sort: string = 'relevance'
  ) => {
    try {
      setLoading(true);
      setError(null);
      setQuery(searchQuery);
      setSelectedCategory(category);
      setSelectedGenre(genre);
      setSortBy(sort as any);

      let searchResults: ContentItem[] = [];

      if (searchQuery.trim()) {
        // Text search
        const searchResponse = await searchContent(searchQuery, { category }, 50);
        searchResults = searchResponse.results as unknown as ContentItem[];
      } else if (genre !== 'all') {
        // Genre-based search
        searchResults = await getContentByGenre(genre, 50, category);
      } else {
        // Category-only search
        searchResults = await getContentByGenre('all', 50, category);
      }

      // Apply sorting
      switch (sort) {
        case 'newest':
          searchResults.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
          break;
        case 'oldest':
          searchResults.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());
          break;
        case 'views':
          searchResults.sort((a, b) => b.views - a.views);
          break;
        case 'likes':
          searchResults.sort((a, b) => b.likes - a.likes);
          break;
        default:
          // Keep relevance order from search service
          break;
      }

      setResults(searchResults);
    } catch (err) {
      console.error('Search error:', err);
      setError('Search failed. Please try again.');
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const searchByGenre = useCallback(async (genre: string, category: string = 'all') => {
    await search('', category, genre, 'newest');
  }, [search]);

  const clearResults = useCallback(() => {
    setResults([]);
    setError(null);
    setQuery('');
  }, []);

  const value: SearchContextValue = {
    results,
    loading,
    error,
    query,
    selectedCategory,
    selectedGenre,
    sortBy,
    search,
    setQuery,
    setCategory: setSelectedCategory,
    setGenre: setSelectedGenre,
    setSortBy: (sortBy: string) => setSortBy(sortBy as any),
    clearResults,
    searchByGenre
  };

  return (
    <SearchContext.Provider value={value}>
      {children}
    </SearchContext.Provider>
  );
};

export const useSearch = (): SearchContextValue => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within SearchProvider');
  }
  return context;
};
